import React, { ChangeEvent, forwardRef, useState } from 'react';
import { Button } from '@/src/features/ui/button/button';
import { CheckIcon, CloseIcon, FilterIcon } from '@/src/features/ui/icons/icons';
import classNames from 'classnames';
import { Pages } from '@/src/interfaces/pages-route';
import { ResponseStoreFrontProductSearchFacet } from '@/src/interfaces/data-contracts';

type FilterFacetPanelProps = {
  facets: Pages.CreditcardListCreate.ResponseBody['facets'][];
  handleSubmit: () => void; // onSubmitHandler will be passed here
  // watchedValues: Record<string, any>;
  register: ReturnType<any>;
  getValues: (name: string) => any;
  setValue: (name: string, value: any) => void;
  // reset: () => void;
  resetAndSubmit: () => void;
  selectedFilterCount: number;
  sortingComponent?: React.ReactNode;
};

type CustomCheckboxProps = {
  label: string;
  isChecked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  props?: React.HTMLAttributes<HTMLInputElement>;
  id: string;
  value: string;
};
const CustomCheckbox = forwardRef<HTMLInputElement, CustomCheckboxProps>(
  ({ label, isChecked, onChange, ...props }, ref) => {
    return (
      <label
        className="flex cursor-pointer select-none items-center bg-white text-sm font-semibold leading-[18px] text-black700"
        data-testid="optionLabel"
      >
        <input ref={ref} className="hidden" type="checkbox" checked={isChecked} onChange={onChange} {...props} />
        <span
          className={classNames('mr-4 flex h-4 w-4 flex-shrink-0 cursor-pointer items-center justify-center md:mr-2', {
            'bg-purple500': isChecked,
            'border border-black400': !isChecked,
            rounded: true,
          })}
        >
          <CheckIcon width="9px" height="6px" className="text-white" />
        </span>
        {label}
      </label>
    );
  }
);

type FacetProps = {
  facet: ResponseStoreFrontProductSearchFacet;
  getValues: (name: string) => any;
  setValue: (name: string, value: any) => void;
  register: any;
};

const Facet: React.FC<FacetProps> = ({ facet, getValues, register, setValue }) => {
  return (
    <div key={facet.code} className="mb-6 mt-5 md:mt-1" data-testid="facetOptionList">
      <h4 className="mb-4 ml-4 text-sm font-bold text-black800 md:mb-2" data-testid="name">
        {facet.name}
      </h4>
      {facet.options?.map((option: any) => {
        const optionCode = option.code;
        const watchedValue = Array.isArray(getValues(facet.code!)) ? getValues(facet.code!) : [];
        const isChecked = watchedValue.includes(optionCode);

        return (
          <div
            key={option.code}
            className="flex justify-between border-b border-b-black200 bg-white px-4 py-3 md:border-none md:py-2"
            data-testid="facetOption"
          >
            <CustomCheckbox
              label={option.name}
              id={option.code}
              value={option.code}
              isChecked={isChecked}
              {...register(facet.code)}
              onChange={(e) => {
                const checked = e.target.checked;
                setValue(
                  facet.code!,
                  checked
                    ? [...(getValues(facet.code!) || []), optionCode]
                    : getValues(facet.code!)?.filter((value: string) => value !== optionCode)
                );
              }}
              data-testid={option.code}
            />
            <div className="text-xs text-black600" data-testid="optionCount">
              {option.count}
            </div>
          </div>
        );
      })}
    </div>
  );
};

CustomCheckbox.displayName = 'CustomCheckbox';
const FilterFacetPanel: React.FC<FilterFacetPanelProps> = ({
  facets,
  handleSubmit,
  register,
  getValues,
  setValue,
  selectedFilterCount,
  resetAndSubmit,
  sortingComponent,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  return (
    <>
      <div className="flex justify-between md:hidden" data-testid="filterPanel">
        <Button
          text="Filtrele"
          isNegative={true}
          paleButton={true}
          onClick={openModal}
          className="h-[40px] w-[165px] rounded-lg border-black100 text-sm md:text-base"
          data-testid="filterButton"
        >
          <FilterIcon width={20} height={20} className="purple500Svg mr-1" data-testid="filterIcon" />
        </Button>
        {sortingComponent}
      </div>
      <div className={`fixed inset-0 z-50 md:z-0 ${isModalOpen ? 'block' : 'hidden'} md:relative md:block`}>
        <div role="none" className="absolute inset-0 bg-black opacity-50 md:hidden" onClick={closeModal}></div>
        <div className="absolute inset-0 bg-white md:relative md:bg-transparent">
          <form
            onSubmit={handleSubmit}
            className="h-full w-full md:w-[256px] md:rounded-xl md:border md:border-black300"
            data-testid="filterForm"
          >
            <div className="flex items-center justify-between p-4">
              <button type="button" onClick={closeModal} className="md:hidden" data-testid="closeButton">
                <CloseIcon width={20} height={20} className="text-black600" />
              </button>
              <h3 className="text-base font-bold text-black1000 md:text-sm">Filtrele</h3>
              <button
                className={classNames('text-sm', selectedFilterCount > 0 ? 'text-purple500' : 'text-purple300')}
                onClick={resetAndSubmit}
                data-testid="clearButton"
              >
                Temizle
              </button>
            </div>
            <div
              className="h-[calc(100vh-120px)] overflow-y-auto bg-black100 md:h-auto md:bg-white"
              data-testid="facetList"
            >
              {facets.map((facet) => {
                if (!(facet?.code && facet.name)) return <></>;
                return <Facet key={facet.code} {...{ facet, getValues, register, setValue }} />;
              })}
            </div>

            <Button
              type="submit"
              text={selectedFilterCount > 0 ? `Uygula (${selectedFilterCount})` : 'Uygula'}
              className={classNames(
                'w-full rounded-lg py-3 text-xs',
                selectedFilterCount > 0 ? 'bg-purple500 text-white' : 'bg-[#EFEFFD] text-[#9E9DF2]'
              )}
              data-testid="submitButton"
            />
          </form>
        </div>
      </div>
    </>
  );
};

export default FilterFacetPanel;
